import { Box, Flex } from '@chakra-ui/react';
import { GetServerSideProps, NextPage } from 'next';

import { EventSessionsDropdown } from '@/components/event/EventSessionsDropdown';
import { EventSearchInput } from '@/components/ui/features/events-list/EventSearchInput/EventSearchInput';
import { SearchQuery } from '@/components/ui/features/schedule/ScheduleQuerySearch/SearchQuery';
import {
  ScheduleTrackFilter,
  TrackFilterScope,
} from '@/components/ui/features/schedule/ScheduleTrackFilter/ScheduleTrackFilter';
import { EventAppLayout } from '@/components/ui/shared/layouts/EventAppLayout/EventAppLayout';
import { NoTicketCheck } from '@/components/ui/shared/overlays/NoTicketCheck/NoTicketCheck';
import { EventSessions } from '@/containers/Event/EventSessions';
import { SearchCategories, SearchGroup } from '@/enums/index';
import { useQueryParams } from '@/hooks/useQueryParams';
import { queryThinEventSessionsByQueryInfinite } from '@/queries/thin-event-sessions';
import { queryThinEvent, queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { generateStore } from '@/utils/generateStore';
import { Hydrate, IHydrateProps } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const EventSessionsPage: NextPage<IHydrateProps> = ({ fallback }) => {
  const { eventId, searchGroup, query } = useQueryParams<{ eventId: string; query: string; searchGroup: string }>({
    eventId: StringParam,
    searchGroup: StringParam,
    query: StringParam,
  });

  const isSessions = searchGroup === SearchGroup.EVENT_SESSION;

  return (
    <Hydrate fallback={fallback}>
      <EventAppLayout>
        <NoTicketCheck eventId={eventId}>
          <Flex
            backgroundColor="white"
            flexDir={{ base: 'column', lg: 'row' }}
            alignItems={{ base: 'flex-start', lg: 'center' }}
            justifyContent="flex-end"
            minH={24}
            py={4}
            px={8}
            position="sticky"
            top={'4.5rem'}
            zIndex={20}
            borderBottomWidth="1px"
            borderBottom="gray.200"
          >
            <Flex flexDirection={{ base: 'column', md: 'row' }} justifyContent="flex-end" gap={4} w="100%">
              <ScheduleTrackFilter scope={TrackFilterScope.Lectures} />
              <EventSessionsDropdown scope={SearchCategories.LECTURES} />
              <EventSearchInput w="100%" maxW="400px" />
            </Flex>
          </Flex>
          {!isSessions && query ? (
            <Box bg="gray.50">
              <SearchQuery />
            </Box>
          ) : (
            <EventSessions scope={SearchCategories.LECTURES} />
          )}
        </NoTicketCheck>
      </EventAppLayout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { eventId, search, searchGroup, trackId } = urlQueryStringValidate<{
    eventId: string;
    search: string;
    searchGroup: string;
    trackId: string;
  }>(context.query, {
    eventId: StringParam,
    search: StringParam,
    searchGroup: StringParam,
  });

  const filters: Record<string, string> = {
    event_id: eventId,
    listed: 'true',
    searchable_attribute: searchGroup === 'event_session' ? 'name' : '',
    query: search,
    track_id: trackId,
    scope: 'with_lectures',
  };

  try {
    const [eventConfigurationResponse] = await Promise.all([
      store.fetchResourceRelationship(queryThinEventConfigurationRelationship(eventId)),
      store.fetchResource(queryThinEvent(eventId)),

      // needed for EventSessions component
      store.fetchResourcesInfinite(
        queryThinEventSessionsByQueryInfinite({
          filters,
        })
      ),
    ]);

    if (!eventConfigurationResponse?.data?.sessionsPage) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        fallback: store.fallback,
      },
    };
  } catch (err) {
    return {
      props: {
        fallback: store.fallback,
      },
    };
  }
};

export default EventSessionsPage;
